import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { TokenStats, TokenAllocation, TokenUsageLog, isTokenAllocation, isTokenUsageLog } from "@/types/tokens";

export function useTokenStats(userId?: string) {
  const [realtimeStats, setRealtimeStats] = useState<TokenStats | null>(null);

  const { data: initialStats, isLoading, error, refetch } = useQuery({
    queryKey: ["tokenStats", userId],
    queryFn: async () => {
      if (!userId) throw new Error("No user ID provided");
      
      console.log("Fetching initial token stats for user:", userId);
      
      const { data: allocation, error: allocationError } = await supabase
        .from("token_allocations")
        .select("monthly_tokens, tokens_used")
        .eq("user_id", userId)
        .maybeSingle();

      if (allocationError) {
        console.error("Error fetching token allocation:", allocationError);
        throw allocationError;
      }

      if (!allocation) {
        console.log("No token allocation found, using defaults");
        return {
          monthlyTokens: 5000,
          tokensUsed: 0,
          recentUsage: 0,
          usagePercentage: 0
        };
      }

      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

      const { data: recentUsage, error: usageError } = await supabase
        .from("token_usage_logs")
        .select("tokens_used")
        .eq("user_id", userId)
        .gte("created_at", twentyFourHoursAgo.toISOString());

      if (usageError) {
        console.error("Error fetching recent usage:", usageError);
        throw usageError;
      }

      const recent = recentUsage?.reduce((sum, log) => sum + (log.tokens_used || 0), 0) ?? 0;
      const usagePercentage = (allocation.tokens_used / allocation.monthly_tokens) * 100;
      
      console.log("Token stats fetched:", {
        monthlyTokens: allocation.monthly_tokens,
        tokensUsed: allocation.tokens_used,
        recentUsage: recent,
        usagePercentage
      });

      return {
        monthlyTokens: allocation.monthly_tokens,
        tokensUsed: allocation.tokens_used,
        recentUsage: recent,
        usagePercentage
      };
    },
    retry: 1,
    enabled: !!userId,
    meta: {
      errorMessage: "Kunne ikke hente token statistikk. Vennligst prøv igjen senere."
    }
  });

  useEffect(() => {
    if (!userId) return;

    console.log("Setting up realtime subscription for user:", userId);

    const channel = supabase
      .channel("token-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "token_allocations",
          filter: `user_id=eq.${userId}`
        },
        (payload: RealtimePostgresChangesPayload<TokenAllocation>) => {
          console.log("Received token allocation update:", payload);
          const newData = payload.new;
          if (newData && isTokenAllocation(newData)) {
            setRealtimeStats(prev => {
              if (!prev) return null;
              const usagePercentage = (newData.tokens_used / newData.monthly_tokens) * 100;
              return {
                ...prev,
                monthlyTokens: newData.monthly_tokens,
                tokensUsed: newData.tokens_used,
                usagePercentage
              };
            });
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "token_usage_logs",
          filter: `user_id=eq.${userId}`
        },
        (payload: RealtimePostgresChangesPayload<TokenUsageLog>) => {
          console.log("Received token usage log:", payload);
          const newData = payload.new;
          if (newData && isTokenUsageLog(newData)) {
            setRealtimeStats(prev => {
              if (!prev) return null;
              return {
                ...prev,
                recentUsage: prev.recentUsage + newData.tokens_used,
                usagePercentage: ((prev.tokensUsed + newData.tokens_used) / prev.monthlyTokens) * 100
              };
            });
          }
        }
      )
      .subscribe();

    if (initialStats) {
      setRealtimeStats(initialStats);
    }

    return () => {
      console.log("Cleaning up realtime subscription");
      supabase.removeChannel(channel);
    };
  }, [userId, initialStats]);

  return {
    stats: realtimeStats || initialStats,
    isLoading,
    error,
    refetch
  };
}